<template>
  <ContentContainer :loadingStatus="isLoading">
    <div class="room-type-list content-wrapper">
      <div class="page-header">{{ $t('transfer') }}
        <popover-preview-remark-component
          :hotel-id="activeHotelId"
          :remark-type="REMARK_TYPE.TRANSFER">
        </popover-preview-remark-component>
      </div>
      <div>
        <el-row>
          <el-col :span="4">
            <el-input :placeholder="$t('keyword')"
                      @input="isSearching = true"
                      v-model="keyword"></el-input>
          </el-col>
          <el-col :span="20">
            <el-button style="float: right;" type="primary" icon="el-icon-plus" @click="onGotoAddButtonClick">{{
              $t('newTransfer') }}</el-button>
          </el-col>
        </el-row>
        <div style="margin-top: 25px">
          <el-table
            id="transfer-list-table"
            :data="transferListDataSource"
            :default-sort = "{prop: 'name', order: 'ascending'}"
            v-loading="isSearching"
            header-row-class-name="table-header-color"
            style="width: 100%">
            <el-table-column
              :label="$t('default')"
              width="90px">
              <template slot-scope="scope">
                <el-checkbox v-model="isDefaultList[scope.row.id]" :label="scope.row.id" @change="onChangeIsDefault(scope.row.id)"> &nbsp; </el-checkbox>
              </template>
            </el-table-column>
            <el-table-column
              prop="name"
              :label="$t('name')">
            </el-table-column>
            <el-table-column
              align="right"
              :label="$t('price')">
              <template slot-scope="scope">{{ bindPrice(scope.row) }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('pax')">
              <template slot-scope="scope">
                <div>Min: {{ scope.row.minPax }}</div>
                <div>Max: {{ scope.row.maxPax }}</div>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('conditionDetail')">
              <template slot-scope="scope">{{ getConditionDetail(scope.row) }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('status')">
              <template slot-scope="scope">
                <el-tag
                  :type="scope.row.isEnable === 1 ? 'success' : 'danger'" size="mini"
                  disable-transitions>{{ getTransferStatus(scope.row.isEnable) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column align="center">
              <template slot-scope="scope">
                <el-button type="primary" size="small" icon="el-icon-edit" plain circle @click="onGotoEditButtonClick(scope.row)"></el-button>
                <el-button type="danger" size="small" icon="el-icon-delete" plain circle @click="onRemoveTransferButtonClick(scope.row)"></el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </ContentContainer>
</template>

<script>
import { STATUS, TRANSFER_TYPE, REMARK_TYPE } from '@/configs/constant'
import i18nMessages from '@/translations/Hotel/ManageHotel/Transfer/List.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import HotelTransferApi from '@/api/hotel/transfer-api'
import ContentContainer from '@/components/ContentContainerComponent.vue'
import PopoverPreviewRemarkComponent from '../../components/PopoverPreviewRemarkComponent'
import LocalStorageFactory from '@/factories/local-storage-factory'
import { getHotelIndexRoute } from '@/services/hotel-service'
import { filterDataByKeyword } from '@/services/general-service'
import { getStatusName } from '@/helpers'
import { mapState } from 'vuex'
import { numberFormat, bindData } from '@/helpers/index'
import { get, debounce } from 'lodash'

export default {
  name: 'HotelTransferList',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  components: { ContentContainer, PopoverPreviewRemarkComponent },
  i18n: { messages: i18nMessages },
  created: async function () {
    if (this.activeHotelId === undefined || this.activeHotelId === '') {
      const companyDetail = LocalStorageFactory.getCompanyDetail()
      const routeName = await getHotelIndexRoute(companyDetail, this.$store)

      return this.$router.push({
        name: routeName
      })
    }

    try {
      this.isLoading = true

      this.transferList = await HotelTransferApi.list(this.activeHotelId)
      this.isDefaultList = this._prepareIsDefaultList()
      this.getTransferListDataSource()
    } catch (err) {
      this.alertError(err.message)
    }

    this.isLoading = false
  },
  computed: {
    ...mapState({
      activeHotelId: state => state.general.activeHotelId
    })
  },
  watch: {
    keyword: debounce(function () {
      this.isSearching = false
    }, 300),
    isSearching: function (value) {
      if (!value) {
        this.getTransferListDataSource()
      }
    }
  },
  data () {
    return {
      REMARK_TYPE,
      STATUS,
      isLoading: true,
      keyword: '',
      isSearching: false,
      transferList: [],
      transferListDataSource: [],
      isDefaultList: {}
    }
  },
  methods: {
    onChangeIsDefault: async function (id) {
      try {
        this.startLoading()

        const isDefault = (this.isDefaultList[id] === true) ? 1 : 0
        await HotelTransferApi.updateIsDefault(this.activeHotelId, id, isDefault)

        const notificationTitle = this.$t('updateIsDefaultTransferSuccess')
        this.showNotificationSuccess(notificationTitle)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    getTransferListDataSource: function () {
      const searchKeys = ['name']
      const filteredTransferList = filterDataByKeyword(this.transferList, this.keyword, searchKeys)
      this.transferListDataSource = [...filteredTransferList]
    },
    getTransferStatus: function (status) {
      return this.$t(getStatusName(status))
    },
    getTransferUnitType: function (unitType) {
      switch (unitType) {
        case TRANSFER_TYPE.PER_CAR:
          return this.$t('transferUnitTypeCar')
        case TRANSFER_TYPE.PER_PERSON:
          return this.$t('transferUnitTypePerPerson')
        default:
          return 'N/A'
      }
    },
    bindPrice: function (row) {
      const suffix = '.-'
      const unitType = this.getTransferUnitType(row.unitType)
      const price = numberFormat(row.price, suffix)
      return `${price} ${unitType}`
    },
    getConditionDetail: function (row) {
      return bindData(row, 'conditionDetail')
    },
    onGotoAddButtonClick: function () {
      this.$router.push({
        name: 'HotelTransferAdd'
      })
    },
    onGotoEditButtonClick: function (transfer) {
      this.$router.push({
        name: 'HotelTransferEdit',
        params: {
          transfer: transfer
        }
      })
    },
    onRemoveTransferButtonClick: async function (row) {
      const confirmMessage = this.$t('confirmRemoveTransferMessage', {
        transferName: row.name
      })
      const confirmButtonText = this.$t('confirmButtonText')
      const cancelButtonText = this.$t('cancelButtonText')
      const isConfirmRemove = await this.alertConfirmDelete(confirmMessage, confirmButtonText, cancelButtonText)

      if (!isConfirmRemove) {
        return false
      }

      this.removeTransfer(row.id)
    },
    removeTransfer: async function (transferId) {
      try {
        this.startLoading()

        await HotelTransferApi.remove(this.activeHotelId, transferId)
        this.transferList = this.transferList.filter(item => item.id !== transferId)
        this.getTransferListDataSource()

        const notificationTitle = this.$t('removeTransferSuccess')
        this.showNotificationSuccess(notificationTitle)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    _prepareIsDefaultList: function () {
      const isDefaultList = {}

      this.transferList.forEach((transfer) => {
        const transferId = transfer.id
        const isDefault = get(transfer, 'isDefault', false)
        isDefaultList[transferId] = Boolean(isDefault)
      })

      return isDefaultList
    }
  }
}
</script>

<style scoped>
  #transfer-list-table {
    width: 100%;
    border: 1px solid #dddddd;
    border-radius: 5px;
    box-shadow: 0px 3px 5px 1px #eeeeee;
  }
  .el-table th {
    background-color: #eeeeee;
  }
  .el-form > div {
    text-align: left;
  }
</style>
